import { useState } from "react";
import type { PaymentMethod } from "@ai-pos/shared";
import { ordersApi, type CheckoutResponse } from "../../api/resources.js";
import { computeCartTotals, useCartStore } from "../../stores/cartStore.js";

interface Props {
  paymentMethods: PaymentMethod[];
  onBack: () => void;
  onCompleted: (result: CheckoutResponse) => void;
}

export function CheckoutConfirmPanel({ paymentMethods, onBack, onCompleted }: Props) {
  const cart = useCartStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totals = computeCartTotals(cart.items, cart.discount);

  const paymentMethodNameById = new Map(paymentMethods.map((m) => [m.id, m.name]));
  // 値引き按分前の素の小計。按分後の正確な内訳は会計確定後にサーバーが返す
  // order.paymentsを使う（フロントとバックエンドで按分計算を二重実装しないため）。
  const subtotalByPaymentMethod = new Map<string, number>();
  for (const item of cart.items) {
    const key = item.paymentMethodId ?? "";
    subtotalByPaymentMethod.set(key, (subtotalByPaymentMethod.get(key) ?? 0) + item.unitPrice * item.quantity);
  }

  const handleConfirm = async () => {
    if (cart.items.some((item) => !item.paymentMethodId)) {
      setError("支払方法を選択してください");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await ordersApi.checkout({
        idempotencyKey: cart.idempotencyKey,
        items: cart.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          note: item.note,
          paymentMethodId: item.paymentMethodId as string,
          unitPrice: item.unitPrice,
        })),
        discount: cart.discount,
        guestCount: cart.guestCount,
        note: cart.note,
      });
      cart.clear();
      onCompleted(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "会計確定に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card stack" style={{ maxWidth: 640, margin: "0 auto" }}>
      <h2>会計内容の確認</h2>
      <p style={{ color: "var(--color-text-muted)" }}>内容をご確認のうえ「会計確定」を押してください。</p>

      <table>
        <thead>
          <tr>
            <th>商品</th>
            <th>数量</th>
            <th>金額</th>
          </tr>
        </thead>
        <tbody>
          {cart.items.map((item) => (
            <tr key={item.rowId}>
              <td>{item.productName}</td>
              <td>{item.quantity}</td>
              <td>¥{(item.unitPrice * item.quantity).toLocaleString("ja-JP")}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cart-summary">
        <div className="cart-summary__row">
          <span>小計</span>
          <span>¥{totals.subtotal.toLocaleString("ja-JP")}</span>
        </div>
        <div className="cart-summary__row">
          <span>値引き</span>
          <span>¥{cart.discount.toLocaleString("ja-JP")}</span>
        </div>
        <div className="cart-summary__row cart-summary__total">
          <span>合計</span>
          <span>¥{totals.total.toLocaleString("ja-JP")}</span>
        </div>
      </div>

      <div className="row row-wrap">
        {subtotalByPaymentMethod.size <= 1 ? (
          <span className="badge">
            支払方法: {paymentMethodNameById.get(cart.items[0]?.paymentMethodId ?? "") ?? "未選択"}
          </span>
        ) : (
          [...subtotalByPaymentMethod.entries()].map(([paymentMethodId, amount]) => (
            <span key={paymentMethodId || "unknown"} className="badge">
              支払方法: {paymentMethodNameById.get(paymentMethodId) ?? "未選択"}（小計 ¥{amount.toLocaleString("ja-JP")}）
            </span>
          ))
        )}
        <span className="badge">人数: {cart.guestCount}名</span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="row">
        <button className="btn" onClick={onBack} disabled={submitting}>
          修正に戻る
        </button>
        <button className="btn btn-danger btn-lg" style={{ flex: 1 }} onClick={() => void handleConfirm()} disabled={submitting}>
          {submitting ? "処理中..." : "会計確定"}
        </button>
      </div>
    </div>
  );
}
